import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {getAuthorisationURLWithQueryParamsAndSetState} from "supertokens-auth-react/recipe/thirdparty";
import Session from "supertokens-auth-react/recipe/session";
import {motion, type Variants} from "motion/react";
import {AlertCircle} from "lucide-react";
import LogoFull from "@/components/icons/logo-full";
import {SparklesCore} from "@/components/ui/shadcn-io/sparkles";
import {Alert, AlertDescription} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Separator} from "@/components/ui/separator";
import {Skeleton} from "@/components/ui/skeleton";
import {Spinner} from "@/components/ui/spinner";
import {PasswordlessForm} from "@/components/auth/passwordless-form";
import {getAuthErrorMessage} from "@/lib/auth-errors";
import * as m from "@/paraglide/messages";

// Animation variants for staggered content reveal
const containerVariants: Variants = {
  hidden: {opacity: 0},
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.15,
      delayChildren: 0.3,
    },
  },
};

const itemVariants: Variants = {
  hidden: {opacity: 0, x: -30},
  visible: {
    opacity: 1,
    x: 0,
    transition: {duration: 0.6, ease: [0.22, 1, 0.36, 1]},
  },
};

const logoVariants: Variants = {
  hidden: {opacity: 0, scale: 0.8},
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      type: "spring",
      stiffness: 200,
      damping: 15,
    },
  },
};

export function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkingSession, setCheckingSession] = useState(true);
  const [loading, setLoading] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const errorParam = searchParams.get("error");
  const errorMessage = errorParam ? getAuthErrorMessage(errorParam) : formError;

  // Check if already logged in
  useEffect(() => {
    Session.doesSessionExist().then((exists) => {
      if (exists) {
        navigate("/favorites", {replace: true});
      } else {
        setCheckingSession(false);
      }
    });
  }, [navigate]);

  // Show skeleton while checking session
  if (checkingSession) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <Skeleton className="h-[400px] w-full max-w-md rounded-xl" />
      </div>
    );
  }

  const handleGoogleLogin = async () => {
    setLoading("google");
    try {
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: "google",
        frontendRedirectURI: `${window.location.origin}/auth/callback/google`,
      });
      window.location.assign(authUrl);
    } catch (err) {
      console.error("Google login error:", err);
      setLoading(null);
    }
  };

  const handlePasswordlessVerified = () => {
    navigate("/favorites", {replace: true});
  };

  const handlePasswordlessError = (message: string) => {
    setFormError(message);
  };

  return (
    <div className="dark bg-background min-h-screen">
      <div className="flex min-h-screen">
        {/* Left side - Branding (hidden on mobile) */}
        <div className="relative hidden items-center justify-center overflow-hidden bg-zinc-900 p-12 lg:flex lg:w-1/2">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-linear-to-t from-zinc-900/80 via-transparent to-zinc-900/40" />

          {/* Animated content */}
          <motion.div
            className="text-foreground relative z-10 flex h-full max-w-md flex-col justify-between py-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Top: Logo and title */}
            <motion.div variants={itemVariants} className="flex flex-col items-center">
              <motion.div variants={logoVariants}>
                <LogoFull className="text-foreground h-12" />
              </motion.div>
              {/* Sparkles animation */}
              <div className="relative mt-4 h-36 w-96">
                {/* Gradient lines (semantic colors) */}
                <div className="via-muted-foreground absolute inset-x-10 top-0 h-[2px] w-3/4 bg-gradient-to-r from-transparent to-transparent blur-sm" />
                <div className="via-muted-foreground absolute inset-x-10 top-0 h-px w-3/4 bg-gradient-to-r from-transparent to-transparent" />
                <div className="via-muted-foreground/70 absolute inset-x-20 top-0 h-[5px] w-1/4 bg-gradient-to-r from-transparent to-transparent blur-sm" />
                <div className="via-muted-foreground/70 absolute inset-x-20 top-0 h-px w-1/4 bg-gradient-to-r from-transparent to-transparent" />
                {/* Sparkles */}
                <SparklesCore
                  background="transparent"
                  minSize={0.4}
                  maxSize={1}
                  particleDensity={800}
                  className="h-full w-full"
                  particleColor="#ffffff"
                />
                {/* Radial gradient mask */}
                <div className="absolute inset-0 h-full w-full bg-zinc-900 [mask-image:radial-gradient(240px_150px_at_top,transparent_20%,white)]" />
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Right side - Auth Form */}
        <div className="flex w-full items-center justify-center p-5 md:p-8 lg:w-1/2">
          <Card className="bg-card/50 w-full max-w-md border-0 shadow-2xl backdrop-blur">
            <CardHeader className="space-y-1 px-4 pb-8 text-center md:px-6">
              {/* Mobile logo */}
              <div className="mb-4 flex justify-center lg:hidden">
                <LogoFull className="text-foreground h-9" />
              </div>
              <CardTitle className="text-2xl font-bold">{m.auth_welcome_back()}</CardTitle>
              <CardDescription>{m.auth_signin_description()}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error Message */}
              {errorMessage && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {/* Google Sign In */}
              <Button
                variant="outline"
                className="h-12 w-full text-base"
                onClick={handleGoogleLogin}
                disabled={loading !== null}
              >
                {loading === "google" ? (
                  <Spinner className="mr-2" />
                ) : (
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                {m.auth_continue_google()}
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-muted-foreground text-xs uppercase">
                  {m.auth_or_continue_email()}
                </span>
                <Separator className="flex-1" />
              </div>

              {/* Passwordless Form (Email + OTP) */}
              <PasswordlessForm
                onVerified={handlePasswordlessVerified}
                onError={handlePasswordlessError}
                disabled={loading !== null}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
